import { FLOW_TYPE, FLOW_TYPE_PARTNER } from "@/constants/analytics.constants";

export type EcommerceItemCategory =
  | "kit"
  | "camera"
  | "sensor"
  | "accessory"
  | "monitoring"
  | "life_safety"
  | "smart_home";

export type EcommerceItem = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_category: EcommerceItemCategory;
  index?: number;
  add_on?: "y" | "n";
  item_list_id?: string;
  item_list_name?: string;
};

export type TotalPrice = {
  base: number;
  monitoring: number;
  add_on: number;
};

export type EcommercePayload = {
  currency?: string;
  value?: number;
  tax?: number;
  shipping?: number;
  transaction_id?: string;
  payment_type?: string;
  ecomm_flow_type: string;
  ecomm_flow_type_partner: string;
  item_list_id?: string;
  item_list_name?: string;
  total_price?: TotalPrice;
  items: EcommerceItem[];
};

const ECOMM_FLOW_TYPE = FLOW_TYPE.BLU_DIY;
const ECOMM_FLOW_TYPE_PARTNER = FLOW_TYPE_PARTNER.ADT;
const roundUsd = (value: number): number => Math.round(value * 100) / 100;

export function buildEcommerceEvent(
  event: string,
  ecommerce: EcommercePayload,
): Record<string, unknown> {
  return {
    event,
    ...(event === "view_item_list" || event === "select_item"
      ? {
          item_list_id: ecommerce.item_list_id,
          item_list_name: ecommerce.item_list_name,
        }
      : {}),
    ecommerce,
  };
}

export function buildSimpleEcommerceEvent(
  event: string,
): Record<string, unknown> {
  return {
    event,
    ecommerce: {
      ecomm_flow_type: ECOMM_FLOW_TYPE,
      ecomm_flow_type_partner: ECOMM_FLOW_TYPE_PARTNER,
    },
  };
}

export function createEcommercePayload(
  items: EcommerceItem[],
  options: {
    value?: number;
    tax?: number;
    shipping?: number;
    transaction_id?: string;
    payment_type?: string;
    item_list_id?: string;
    item_list_name?: string;
    total_price?: TotalPrice;
  } = {},
): EcommercePayload {
  return {
    currency: "USD",
    ...(options.value !== undefined && { value: options.value }),
    ...(options.tax !== undefined && { tax: options.tax }),
    ...(options.shipping !== undefined && { shipping: options.shipping }),
    ...(options.transaction_id && { transaction_id: options.transaction_id }),
    ...(options.payment_type && { payment_type: options.payment_type }),
    ecomm_flow_type: ECOMM_FLOW_TYPE,
    ecomm_flow_type_partner: ECOMM_FLOW_TYPE_PARTNER,
    ...(options.item_list_id && { item_list_id: options.item_list_id }),
    ...(options.item_list_name && { item_list_name: options.item_list_name }),
    ...(options.total_price && { total_price: options.total_price }),
    items,
  };
}

export function mapProductToItem(
  product: {
    id?: string;
    slug?: string;
    name?: string;
    listPrice?: number | null;
    salesPrice?: number | null;
    type?: string | null;
  },
  options: {
    index?: number;
    quantity?: number;
    category?: EcommerceItemCategory;
    add_on?: "y" | "n";
    item_list_id?: string;
    item_list_name?: string;
  } = {},
): EcommerceItem {
  return {
    item_id: product.id || product.slug || "",
    item_name: (product.name || "").trim(),
    price: product.salesPrice ?? product.listPrice ?? 0,
    quantity: options.quantity ?? 1,
    item_category: options.category ?? resolveEcommerceItemCategory(product),
    ...(options.index !== undefined && { index: options.index }),
    ...(options.add_on && { add_on: options.add_on }),
    ...(options.item_list_id && { item_list_id: options.item_list_id }),
    ...(options.item_list_name && { item_list_name: options.item_list_name }),
  };
}

export function buildItemListId(category: string, prefix = "blu_plp"): string {
  return `${prefix}_${category.toLowerCase()}`;
}

export function buildItemListName(category: string): string {
  if (!category) return "All";
  return category.charAt(0).toUpperCase() + category.slice(1);
}

export type AnalyticsLineItem = {
  type?: "kit" | "accessory" | "camera" | "monitoring";
  productId?: string;
  productName?: string;
  quantity?: number;
  listPrice: number;
  salesPrice: number;
};

export type AddToCartLineItem = AnalyticsLineItem & {
  bonusLineItem?: boolean;
};

const PRODUCT_CATEGORY_BY_ID: Record<string, EcommerceItemCategory> = {
  "build-my-system": "kit",
  "security-starter-kit": "kit",
  "life-safety-kit": "kit",
  "video-essentials-kit": "kit",
  "outdoor-video-kit": "kit",
  "total-safety-kit": "kit",

  "indoor-camera": "camera",
  "outdoor-camera": "camera",
  "doorbell-camera": "camera",

  "yard-sign": "accessory",
  "live-light": "accessory",
  "window-stickers-pack-of-4": "accessory",
  "door-window-sensor-spacer": "accessory",
  "door-window-sensor-cover": "accessory",
  "door-window-sensor-premium-cover": "accessory",
  "indoor-siren": "accessory",
  "keychain-remote": "accessory",

  "smoke-carbon-monoxide-detector-combo": "life_safety",
  "smoke-detector": "life_safety",

  "door-window-sensor": "sensor",
  "door-window-sensor-premium": "sensor",
  "motion-sensor": "sensor",
  "glass-break-sensor": "sensor",
  "water-and-temperature-sensor": "sensor",

  "yale-assure-lock-2": "smart_home",
  "yale-assure-lock-2-satin-nickel": "smart_home",
  "yale-assure-lock-2-black-suede": "smart_home",
  "keypad-with-stand": "smart_home",
  "google-nest-hub": "smart_home",
  "google-nest-thermostat": "smart_home",
  "google-nest-learning-thermostat": "smart_home",

  "self-protect": "monitoring",
  "video-connect": "monitoring",
  "pro-protect": "monitoring",
  "pro-protect-life-safety": "monitoring",
  "pro-protect-with-video": "monitoring",
  "pro-protect-with-video-life-safety": "monitoring",
};

export function resolveEcommerceItemCategory(product: {
  id?: string | null;
  productId?: string | null;
  slug?: string | null;
  type?: string | null;
}): EcommerceItemCategory {
  const productId = product.id ?? product.productId ?? product.slug;
  if (productId && PRODUCT_CATEGORY_BY_ID[productId]) {
    return PRODUCT_CATEGORY_BY_ID[productId];
  }

  return (product.type ?? "camera") as EcommerceItemCategory;
}

export function mapProductTypeToAnalyticsCategory(
  type?: string | null,
  productId?: string | null,
): EcommerceItemCategory {
  return resolveEcommerceItemCategory({ id: productId, type });
}

export function buildLineItemsTotalPrice(
  kits: AnalyticsLineItem[],
  accessories: AnalyticsLineItem[],
  monitoring: AnalyticsLineItem | null,
): TotalPrice {
  const hasBaseKit = hasKitLine(kits);
  const kitTotal = sumLineAnalyticsPrice(kits);
  const accessoryTotal = sumLineAnalyticsPrice(accessories);
  const mon = monitoring ? monitoring.salesPrice : 0;

  return {
    base: roundUsd(kitTotal + (hasBaseKit ? 0 : accessoryTotal)),
    monitoring: roundUsd(mon),
    add_on: roundUsd(hasBaseKit ? accessoryTotal : 0),
  };
}

export function buildCartEcommercePayload(
  items: {
    kits: AnalyticsLineItem[];
    accessories: AnalyticsLineItem[];
    monitoring: AnalyticsLineItem | null;
  },
  options: {
    tax?: number;
    shipping?: number;
    item_list_id?: string;
    item_list_name?: string;
    transaction_id?: string;
    payment_type?: string;
    includeIndex?: boolean;
  } = {},
): EcommercePayload {
  const totalPrice = buildLineItemsTotalPrice(
    items.kits,
    items.accessories,
    items.monitoring,
  );

  return createEcommercePayload(
    buildLineItems(items.kits, items.accessories, items.monitoring, {
      includeIndex: options.includeIndex,
      includeAddOn: true,
    }),
    {
      value: roundUsd(
        totalPrice.base + totalPrice.monitoring + totalPrice.add_on,
      ),
      tax: options.tax,
      shipping: options.shipping,
      item_list_id: options.item_list_id,
      item_list_name: options.item_list_name,
      transaction_id: options.transaction_id,
      payment_type: options.payment_type,
      total_price: totalPrice,
    },
  );
}

export function buildAddToCartEcommercePayload(
  items: {
    kits: AddToCartLineItem[];
    accessories: AddToCartLineItem[];
    monitoring: AddToCartLineItem | null;
  },
  options: {
    addedProductId?: string;
    tax?: number;
    shipping?: number;
    item_list_id?: string;
    item_list_name?: string;
  } = {},
): EcommercePayload {
  const addedKit = options.addedProductId
    ? items.kits.find((k) => k.productId === options.addedProductId)
    : undefined;
  const addedAccessory = options.addedProductId
    ? items.accessories.find((a) => a.productId === options.addedProductId)
    : undefined;
  const bonusAccessories = options.addedProductId
    ? items.accessories.filter(
        (a) =>
          a.productId !== options.addedProductId &&
          (a.bonusLineItem || a.salesPrice === 0),
      )
    : [];
  const eventKits = options.addedProductId
    ? addedKit
      ? [addedKit]
      : []
    : items.kits;
  const eventAccessories = options.addedProductId
    ? addedAccessory
      ? [addedAccessory, ...bonusAccessories]
      : bonusAccessories
    : items.accessories;
  const addedLine = addedKit ?? addedAccessory;
  const hasBaseKit = hasKitLine(items.kits);
  const eventKitTotal = sumLineAnalyticsPrice(
    eventKits,
    Boolean(options.addedProductId),
  );
  const eventAccessoryTotal = sumLineAnalyticsPrice(
    eventAccessories,
    Boolean(options.addedProductId),
  );
  const totalPrice: TotalPrice = {
    base: roundUsd(eventKitTotal + (hasBaseKit ? 0 : eventAccessoryTotal)),
    monitoring: roundUsd(items.monitoring?.salesPrice ?? 0),
    add_on: roundUsd(hasBaseKit ? eventAccessoryTotal : 0),
  };
  const eventValue =
    options.addedProductId && addedLine
      ? getLineUnitPrice(addedLine) + totalPrice.monitoring
      : totalPrice.base + totalPrice.monitoring + totalPrice.add_on;

  const ecommerceItems: EcommerceItem[] = [
    ...eventKits.map((kit, index) =>
      mapProductToItem(
        {
          id: kit.productId,
          name: kit.productName,
          salesPrice: getLineUnitPrice(kit),
          listPrice: kit.listPrice,
          type: kit.type,
        },
        {
          index,
          quantity: options.addedProductId ? 1 : (kit.quantity ?? 1),
          add_on: "n",
          category: resolveEcommerceItemCategory({
            productId: kit.productId,
            type: kit.type,
          }),
        },
      ),
    ),
    ...eventAccessories.map((accessory, index) =>
      mapProductToItem(
        {
          id: accessory.productId,
          name: accessory.productName,
          salesPrice: getLineUnitPrice(accessory),
          listPrice: accessory.listPrice,
          type: accessory.type,
        },
        {
          index: eventKits.length + index,
          quantity: options.addedProductId ? 1 : (accessory.quantity ?? 1),
          add_on: hasBaseKit ? "y" : "n",
          category: resolveEcommerceItemCategory({
            productId: accessory.productId,
            type: accessory.type,
          }),
        },
      ),
    ),
    ...(items.monitoring
      ? [
          mapProductToItem(
            {
              id: items.monitoring.productId,
              name: items.monitoring.productName,
              salesPrice: getLineUnitPrice(items.monitoring),
              listPrice: items.monitoring.listPrice,
              type: items.monitoring.type,
            },
            {
              index: eventKits.length + eventAccessories.length,
              quantity: items.monitoring.quantity ?? 1,
              add_on: "n",
              category: "monitoring",
            },
          ),
        ]
      : []),
  ];

  return createEcommercePayload(ecommerceItems, {
    value: roundUsd(eventValue),
    tax: options.tax,
    shipping: options.shipping,
    item_list_id: options.item_list_id,
    item_list_name: options.item_list_name,
    total_price: totalPrice,
  });
}

export function buildLineItems(
  kits: AnalyticsLineItem[],
  accessories: AnalyticsLineItem[],
  monitoring: AnalyticsLineItem | null,
  options: { includeIndex?: boolean; includeAddOn?: boolean } = {},
): EcommerceItem[] {
  const { includeIndex = false, includeAddOn = false } = options;
  const hasKit = includeAddOn && hasKitLine(kits);
  const items: EcommerceItem[] = [];

  kits.forEach((kit, index) => {
    const qty = kit.quantity ?? 1;
    items.push({
      item_id: kit.productId ?? "",
      item_name: (kit.productName ?? "").trim(),
      price: qty > 1 ? kit.salesPrice / qty : kit.salesPrice,
      quantity: qty,
      item_category: resolveEcommerceItemCategory({
        productId: kit.productId,
        type: kit.type,
      }),
      ...(includeIndex && { index }),
      ...(includeAddOn && { add_on: "n" as const }),
    });
  });

  accessories.forEach((acc, index) => {
    const qty = acc.quantity ?? 1;
    items.push({
      item_id: acc.productId ?? "",
      item_name: (acc.productName ?? "").trim(),
      price: qty > 1 ? acc.salesPrice / qty : acc.salesPrice,
      quantity: qty,
      item_category: resolveEcommerceItemCategory({
        productId: acc.productId,
        type: acc.type,
      }),
      ...(includeIndex && { index: kits.length + index }),
      ...(includeAddOn && { add_on: (hasKit ? "y" : "n") as "y" | "n" }),
    });
  });

  if (monitoring) {
    const qty = monitoring.quantity ?? 1;
    items.push({
      item_id: monitoring.productId ?? "",
      item_name: (monitoring.productName ?? "").trim(),
      price: qty > 1 ? monitoring.salesPrice / qty : monitoring.salesPrice,
      quantity: qty,
      item_category: "monitoring",
      ...(includeIndex && { index: kits.length + accessories.length }),
      ...(includeAddOn && { add_on: "n" as const }),
    });
  }

  return items;
}

function getLineUnitPrice(item: AnalyticsLineItem): number {
  const quantity = item.quantity ?? 1;
  return quantity > 1 ? item.salesPrice / quantity : item.salesPrice;
}

function sumLineAnalyticsPrice(
  items: AnalyticsLineItem[],
  forceUnitPrice?: boolean,
): number {
  return items.reduce(
    (sum, item) =>
      sum + (forceUnitPrice ? getLineUnitPrice(item) : item.salesPrice),
    0,
  );
}

function hasKitLine(items: AnalyticsLineItem[]): boolean {
  return items.some(
    (item) =>
      resolveEcommerceItemCategory({
        productId: item.productId,
        type: item.type,
      }) === "kit",
  );
}
